import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LaunchExpense } from '@evogard/contract';
import {
  EXPENSE_LAUNCH_ACTION,
  EXPENSE_REQUEST_RESOURCES,
  EXPENSE_SUPERVISE_ACTION,
  loadFailureMessage,
  type ExpenseRequestStage,
  type ExpenseSupervisorAct,
} from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { can, useSessionContext } from '../../../lib/session.js';

const KEY = ['claims', 'expenses'] as const;

/**
 * O que esta pessoa faz com a despesa do departamento — a aba aparece para
 * quem lança OU supervisiona ali. Esconder não é proteger: a API cobra o
 * mesmo par.
 */
export function useExpenseGrants(stage: ExpenseRequestStage | null) {
  const { data: context } = useSessionContext();
  if (stage === null) return { launch: false, supervise: false };
  const resource = EXPENSE_REQUEST_RESOURCES[stage];
  return {
    launch: can(context, resource, EXPENSE_LAUNCH_ACTION),
    supervise: can(context, resource, EXPENSE_SUPERVISE_ACTION),
  };
}

/** As despesas do card — a aba Despesas. No sinistro, as de todos os departamentos. */
export function useCardExpenses(
  stage: ExpenseRequestStage | null,
  originId: number | null,
  enabled: boolean,
) {
  const { data: context } = useSessionContext();
  return useQuery({
    queryKey: [...KEY, 'card', context?.currentTenant.id, stage, originId],
    enabled: enabled && stage !== null && originId !== null,
    queryFn: async () =>
      ok(
        await api.claimExpenses.cardExpenses({
          params: { stage: stage!, originId: String(originId) },
        }),
        200,
        loadFailureMessage(undefined, 'as despesas do card'),
      ),
  });
}

/** O quadro do supervisor de um departamento. */
export function useSupervisorBoard(stage: ExpenseRequestStage) {
  const { data: context } = useSessionContext();
  return useQuery({
    queryKey: [...KEY, 'board', context?.currentTenant.id, stage],
    queryFn: async () =>
      ok(
        await api.claimExpenses.supervisorBoard({ params: { stage } }),
        200,
        loadFailureMessage(undefined, 'o quadro de despesas'),
      ),
  });
}

/** Lançar e decidir — as duas recarregam a aba e o quadro. */
export function useClaimExpenseMutations() {
  const client = useQueryClient();
  const onSettled = () => {
    void client.invalidateQueries({ queryKey: KEY });
    /* A despesa do Pagamento de Débito é um débito (29/10/2026): lançar,
       reprovar ou recusar muda a conta da Indenização. */
    void client.invalidateQueries({ queryKey: ['sinistros', 'sinistro', 'indenizacao'] });
  };
  const launch = useMutation({
    mutationFn: async ({
      stage,
      originId,
      body,
    }: {
      stage: ExpenseRequestStage;
      originId: number;
      body: LaunchExpense;
    }) => {
      return ok(
        await api.claimExpenses.launchExpense({
          params: { stage, originId: String(originId) },
          body,
        }),
        201,
        'Não foi possível lançar a despesa.',
      );
    },
    onSettled,
  });
  const act = useMutation({
    mutationFn: async ({
      id,
      act,
      reason,
    }: {
      id: number;
      act: ExpenseSupervisorAct;
      reason: string | null;
    }) =>
      ok(
        await api.claimExpenses.supervisorAct({ params: { id }, body: { act, reason } }),
        200,
        'Não foi possível concluir.',
      ),
    onSettled,
  });
  return { launch, act };
}
